import { useFormContext, useWatch } from 'react-hook-form';
import React, { useEffect } from 'react';
import {
  khachHangSearchColumns,
  MA_CHUNG_TU,
  mauSoHoaDonSearchColumns,
  QUERY_KEYS,
  taiKhoanSearchColumns
} from '@/constants';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import { AccountModel, KhachHang, MauSoHoaDon } from '@/types/schemas';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

const rowClassName = 'flex items-center gap-2 xl:gap-3';
const leftLabelClassName = 'w-40 shrink-0 text-[13px]';
const middleLabelClassName = 'w-[122px] shrink-0 text-[13px] xl:w-[132px]';
const rightLabelClassName = 'w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28';

export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const { setValue } = useFormContext();
  const ngay_ct = useWatch({ name: 'ngay_ct' });
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: row => {
      if (row) actions.setTaiKhoan(row);
    }
  });

  useEffect(() => {
    if (ngay_ct) {
      setValue('ngay_ct0', ngay_ct);
    }
  }, [ngay_ct, setValue]);

  return (
    <div className='overflow-x-auto bg-white'>
      <div className='min-w-[1400px] px-3 pb-2 pt-1.5 xl:min-w-0 xl:px-4 xl:pb-3 xl:pt-2'>
        <div className='grid grid-cols-12 gap-4 xl:gap-6'>
          {/* Column 1: Client and Bill general details */}
          <div className='col-span-6 min-w-0 space-y-1.5'>
            {/* Loại hóa đơn */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Loại hóa đơn</Label>
              <FormFieldV2
                name='ma_ngv'
                type='select'
                className='w-[200px]'
                disabled={formMode === 'view'}
                options={[
                  { value: '1', label: '1. Giảm giá' },
                  { value: '2', label: '2. Trả lại' },
                  { value: '3', label: '3. Chiết khấu cuối kỳ' }
                ]}
              />
            </div>

            {/* Mã khách hàng & Mã số thuế */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã khách hàng</Label>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <SearchField<KhachHang>
                  type='text'
                  name='ma_kh'
                  errorDisplay='tooltip'
                  value={state.khachHang?.customer_code || ''}
                  searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
                  searchColumns={khachHangSearchColumns}
                  onRowSelection={actions.setKhachHang}
                  columnDisplay='customer_code'
                  dialogTitle='Danh mục khách hàng'
                  className='w-48'
                  disabled={formMode === 'view'}
                />

                <Label className='w-20 shrink-0 text-[13px]'>Mã số thuế</Label>
                <FormFieldV2
                  name='ma_so_thue'
                  type='text'
                  disabled={formMode === 'view'}
                  placeholder='Nhập và tra cứu'
                  value={state.khachHang?.tax_code || ''}
                  className='w-48'
                />
              </div>
            </div>

            {/* Tên khách hàng */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tên khách hàng</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='ten_kh_thue'
                  type='text'
                  disabled={formMode === 'view'}
                  placeholder='Nhập tên khách hàng/đơn vị'
                  value={state.khachHang?.customer_name || ''}
                />
              </div>
            </div>

            {/* Địa chỉ */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Địa chỉ</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='dia_chi'
                  type='text'
                  disabled={formMode === 'view'}
                  placeholder='Nhập địa chỉ khách hàng'
                  value={state.khachHang?.address || ''}
                />
              </div>
            </div>

            {/* Tài khoản có */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tài khoản có</Label>
              <SearchField<AccountModel>
                type='text'
                name='tk_co'
                errorDisplay='tooltip'
                displayRelatedField='name'
                columnDisplay='code'
                searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}`}
                searchColumns={taiKhoanSearchColumns}
                value={state.taiKhoan?.code || state.khachHang?.account_data?.code || ''}
                relatedFieldValue={state.taiKhoan?.name || state.khachHang?.account_data?.name || ''}
                onRowSelection={handleAccountSelection}
                disabled={formMode === 'view'}
                dialogTitle='Danh mục tài khoản'
                className='w-full'
              />
            </div>

            {/* Mẫu hóa đơn */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mẫu hóa đơn</Label>
              <div className='min-w-0 flex-1'>
                <SearchField<MauSoHoaDon>
                  type='text'
                  name='ma_mau_so'
                  errorDisplay='tooltip'
                  displayRelatedField='ten_mau_so'
                  columnDisplay='ma_mau_so'
                  searchEndpoint={`/${QUERY_KEYS.MAU_SO_HOA_DON}`}
                  searchColumns={mauSoHoaDonSearchColumns}
                  value={state.mauHoaDon?.ma_mau_so || ''}
                  relatedFieldValue={state.mauHoaDon?.ten_mau_so || ''}
                  onRowSelection={actions.setMauHoaDon}
                  disabled={formMode === 'view'}
                  dialogTitle='Danh mục mẫu hóa đơn'
                  className='w-full'
                />
              </div>
            </div>

            {/* Diễn giải */}
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Diễn giải</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='dien_giai'
                  type='text'
                  disabled={formMode === 'view'}
                  value={state.dienGiai || ''}
                  onValueChange={e => actions.setDienGiai(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Column 2: Additional buyer info */}
          <div className='col-span-3 min-w-0 space-y-1.5'>
            {/* Người mua hàng */}
            <FormFieldV2
              name='ong_ba'
              label='Người mua hàng'
              labelClassName={middleLabelClassName}
              fieldClassName='w-full'
              disabled={formMode === 'view'}
              className='w-full'
              value={state.khachHang?.contact_person || ''}
              placeholder='Người mua hàng'
            />

            {/* Email */}
            <FormFieldV2
              name='e_mail'
              label='Email'
              labelClassName={middleLabelClassName}
              fieldClassName='w-full'
              className='w-full'
              disabled={formMode === 'view'}
              placeholder='Nhập email'
              value={state.khachHang?.email || ''}
            />
          </div>

          {/* Column 3: Document Numbering, Dates and Status */}
          <div className='col-span-3 ml-auto w-full min-w-0 max-w-[300px] space-y-1.5'>
            {/* Quyển chứng từ / Số chứng từ */}
            <DocumentNumberFieldV2
              ma_ct={MA_CHUNG_TU.BAN_HANG.HOA_DON_DICH_VU_TRA_LAI_GIAM_GIA}
              name='so_ct'
              onQuyenChungTuChange={actions.setQuyenChungTu}
              quyenChungTu={state.quyenChungTu}
              soChungTu={state.soChungTuHienTai}
              onSoChungTuChange={actions.setSoChungTuHienTai}
              disabled={formMode === 'view'}
              formMode={formMode}
              className='flex items-center gap-3'
              labelClassName={rightLabelClassName}
              classNameSearchField='min-w-0 flex-1'
            />

            {/* Ngày chứng từ */}
            <FormFieldV2
              label='Ngày chứng từ'
              name='ngay_ct'
              type='date'
              disabled={formMode === 'view'}
              className='w-full'
              labelClassName={rightLabelClassName}
            />

            {/* Số hóa đơn */}
            <FormFieldV2
              label='Số hóa đơn'
              name='so_ct0'
              type='text'
              disabled={formMode === 'view'}
              className='w-full'
              labelClassName={rightLabelClassName}
            />

            {/* Ký hiệu (đầu vào) */}
            <FormFieldV2
              label='Ký hiệu (đầu vào)'
              name='so_ct2'
              type='text'
              disabled={formMode === 'view'}
              className='w-full'
              labelClassName={rightLabelClassName}
            />

            {/* Ký hiệu (đầu ra) */}
            <FormFieldV2
              label='Ký hiệu (đầu ra)'
              name='so_ct3'
              type='text'
              disabled={formMode === 'view'}
              className='w-full'
              labelClassName={rightLabelClassName}
            />

            {/* Ngày hóa đơn */}
            <FormFieldV2
              label='Ngày hóa đơn'
              name='ngay_ct0'
              type='date'
              disabled={formMode === 'view'}
              className='w-full'
              labelClassName={rightLabelClassName}
            />

            {/* Ngoại tệ & Tỷ giá */}
            <CurrencyInputV2
              formMode={formMode}
              className='flex w-full items-center gap-3'
              labelClassName={rightLabelClassName}
              classNameInput='min-w-0 flex-1'
            />

            {/* Trạng thái */}
            <FormFieldV2
              name='status'
              type='select'
              label='Trạng thái'
              disabled={formMode === 'view'}
              className='w-full'
              labelClassName={rightLabelClassName}
              fieldClassName='w-full'
              options={[
                { value: '0', label: 'Chưa ghi sổ' },
                { value: '3', label: 'Đã ghi sổ' },
                { value: '5', label: 'Chờ duyệt' },
                { value: '9', label: 'Hủy' }
              ]}
            />

            {/* Recorded data checkbox */}
            <div className='flex items-center gap-2 pt-1.5'>
              <div className='shrink-0 xl:w-[104px] 2xl:w-28' />
              <FormFieldV2
                label='Dữ liệu được ghi nhận'
                name='transfer_yn'
                type='checkbox'
                className='w-full'
                labelClassName='text-[13px] font-medium'
                disabled
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BasicInfoTab;
